import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { coercePermissionIdentifiersList } from "@utils/permissionIdentifiers";

interface UserType {
	id: number;
	uuid: string;
	createdAt: string;
	updatedAt: string;
	name: string;
}

interface User {
	id: string | number;
	uuid?: string;
	emailAddress: string;
	phoneNumber: string;
	firstName: string;
	lastName: string;
	isActive: boolean;
	isVerified?: boolean;
	role?: string; // Optional since it might not be in API response
	userType?: UserType; // Optional since it might not be in API response
	organizationTypeId?: number | string | null;
	organizationId?: number | string | null;
	organizationName?: string | null;
	// New fields for roles and permissions
	roles?: string[]; // Array of role identifiers from API
	permissions?: string[]; // Array of permission identifiers from API
	/** Set from login API when user should see the CM dashboard. */
	isCmDashboard?: boolean;
	createdAt: string;
	updatedAt: string;
	signature?: string;
	isSignatureSet?: boolean;
}

interface UserState {
	user: User | null;
	// role?: string; // Optional since it might not be in API response
	token: string | null;
	isAuthenticated: boolean;
	expireTimer: string | number;
	resetToken: string | null;
}

const initialState: UserState = {
	user: null,
	token: null,
	isAuthenticated: false,
	expireTimer: "",
	resetToken: null,
};

function withNormalizedPermissions(user: User): User {
	if (!user.permissions?.length) return user;
	return { ...user, permissions: coercePermissionIdentifiersList(user.permissions) };
}

const userSlice = createSlice({
	name: "user",
	initialState,
	reducers: {
		setUser: (state, action: PayloadAction<{ user: User; token: string }>) => {
			state.user = withNormalizedPermissions(action.payload.user);
			state.token = action.payload.token;
			state.isAuthenticated = true;
		},
		clearUser: state => {
			state.user = null;
			state.token = null;
			state.isAuthenticated = false;
		},
		setExpireTimer: (state, action: PayloadAction<string>) => {
			state.expireTimer = action.payload;
		},
		setResetToken: (state, action: PayloadAction<string>) => {
			state.resetToken = action.payload;
		},
		initializeUser: (state, action: PayloadAction<{ user: User | null; token: string | null }>) => {
			const u = action.payload.user;
			state.user = u ? withNormalizedPermissions(u) : null;
			state.token = action.payload.token;
			state.isAuthenticated = !!action.payload.token;
		},
	},
});

export const { setUser, clearUser, setExpireTimer, setResetToken, initializeUser } = userSlice.actions;
export default userSlice.reducer;
